/* eslint-disable max-lines-per-function */
import React, { useContext } from "react";
import { useFormikContext } from "formik";
import { Link, MatchRenderProps } from "@reach/router";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import { useViewer } from "components/viewer/context";

import { useStyles } from "pages/eligibility/styles";
import { FormValues } from "pages/eligibility/type";
import { QuestionProps } from "pages/eligibility/components/question";
import SaveProgress from "pages/eligibility/components/context/save-progress";
import styles from "../../layout.module.css";

import { keyFromIdentity, findMatchingSection, getErrorCount } from "../helpers";
import { MobileMenu } from "./form-navigation";

type groupProps = {
  name: string;
  title: string;
  questions: string[][];
}

type MenuProps = {
  mobileMenu: MobileMenu;
  groups: groupProps[];
  section: string | undefined;
  path: MatchRenderProps<{ submissionId: string; }>;
  questionsMap: Record<string, QuestionProps>;
}
type SectionQuestions = Record<string, string>;

const FormSidebar: React.FC<MenuProps> = ({ groups, section, path, questionsMap, mobileMenu }) => {
  const classes = useStyles();
  const viewer = useViewer();

  const form = useFormikContext<FormValues>();

  const addSectionQuestion =
  (acc: SectionQuestions, questionIdentity: string[], name: string): SectionQuestions => {
    const questionName = keyFromIdentity(questionIdentity);
    const { children } = questionsMap[questionName];

    return children.reduce(
      (childAcc: SectionQuestions, childIdentity: string[]) =>
        addSectionQuestion(childAcc, childIdentity, name),
      { ...acc, [questionName]: name },
    );
  };

  const questionSections = groups.reduce((acc: SectionQuestions, { name, questions }: groupProps) =>
    questions.reduce((childAcc, questionIdentity) =>
      addSectionQuestion(childAcc, questionIdentity, name), acc), {});

  const questionIdentities = Object.keys(questionSections);

  const sectionErrorCounts = Object.keys(form.errors)
    .reduce((errors, errIdentity) => {
      const sectionName = findMatchingSection(questionIdentities, questionSections, errIdentity);


      return (
        { ...errors,
          [sectionName]: (errors[sectionName] || 0) + getErrorCount(form.errors[errIdentity]) }
      );
    }, {} as Record<string, number>);

  const saveProgress = useContext(SaveProgress);

  return (
    <div>
      <div className={classes.toolbar}>
        <Typography variant="h6">Your Application</Typography>
      </div>

      <Divider />

      <List dense style={{ minHeight: "calc(100vh - 127px)" }}>
        {groups.map(({ name, title: text }: { name: string, title: string }) => (
          <Link key={`section-${name}`} to={`/application/${path.match?.submissionId}/${name}`}>
            <ListItem
              button
              style={{ flexDirection: "row" }}
              selected={name === section}
              onClick={() => {
                if (mobileMenu.isMobile) {
                  mobileMenu.toggle();
                }
                if (viewer) saveProgress();
              }}>
              <ListItemText primary={text} />

              <ListItemIcon>
                {sectionErrorCounts && (
                  <div className="color-hc-error">
                    {sectionErrorCounts[name]}
                  </div>
                )}
              </ListItemIcon>
            </ListItem>
          </Link>
        ))}
      </List>
      <div className={styles.footer} style={{ position: "sticky" }}>&nbsp;</div>
    </div>
  );
};

export default FormSidebar;
